import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";

export type UserRole =
  | "superadmin"
  | "admin"
  | "merchant"
  | "customer";

interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth on mount
    const storedToken = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("user_data");

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_data");
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Direct login - no API call required
      if (email && password) {
        const mockUserData = {
          token: "mock-auth-token-" + Date.now(),
          user: {
            id: "1",
            email: email,
            name: "Admin User",
            role: "admin" as UserRole,
          },
        };

        setToken(mockUserData.token);
        setUser(mockUserData.user);

        // Store in localStorage
        localStorage.setItem("auth_token", mockUserData.token);
        localStorage.setItem("user_data", JSON.stringify(mockUserData.user));

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user?.role ? roles.includes(user.role) : false;
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    const rolePermissions: Record<UserRole, string[]> = {
      superadmin: [
        "all",
        "dashboard",
        "payments",
        "ecommerce",
        "inventory",
        "tenants",
        "analytics",
        "reports",
        "settings",
        "users",
        "support",
        "security",
      ],
      admin: [
        "dashboard",
        "payments",
        "ecommerce",
        "inventory",
        "tenants",
        "analytics",
        "reports",
        "settings",
        "support",
        "security",
      ],
      merchant: ["dashboard", "ecommerce", "inventory", "analytics", "reports"],
      customer: ["ecommerce", "vendor"],
    };

    const permissions = rolePermissions[user.role] || [];
    return permissions.includes("all") || permissions.includes(permission);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    loading,
    hasRole,
    hasAnyRole,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// API helper functions
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  verifyToken: async (token: string) => {
    const response = await fetch("/api/auth/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  refreshToken: async (token: string) => {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },
};
